// TIP-610 — downloading the delivered PDF.
//
//   GET /api/briefs/orders/<reference>/download/
//
// The endpoint answers with the FILE, not a link to it: a Django FileResponse
// carrying `Content-Disposition: attachment; filename="…"`. Two consequences
// shape everything here.
//
//   1. There is no URL to put in an href. The bytes arrive in the response, so
//      the browser has to be handed a blob — which is why saveBlob exists
//      rather than the row linking straight to the endpoint. It also means the
//      request carries the Authorization header, which a plain <a href> could
//      not, and the masked S3 token is never exposed to the page.
//   2. The old gate `downloadUrl !== null` can never open. List rows carry no
//      `download_url` (see briefsApiMapping.js), so the button was unreachable.
//      Readiness is the gate now, and the server checks it again.
//
// No React and no store, so all of this can be pinned by a test.
import { TABLE_COPY } from "./briefsFixtures";

/**
 * The filename the server chose, out of the Content-Disposition header.
 *
 * RFC 5987's `filename*=UTF-8''…` form is preferred when present — it is the
 * one that survives a non-ASCII company name — and the quoted `filename=` is
 * the fallback. A header we cannot read falls back to the reference, so the
 * customer never ends up with a file called "download".
 */
export const filenameFromDisposition = (header, reference) => {
  const fallback = `${reference || "counterparty-brief"}.pdf`;
  if (!header || typeof header !== "string") return fallback;

  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded) return decoded;
    } catch {
      // A malformed percent-escape is not worth failing a download over.
    }
  }

  const plain = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(header);
  const name = (plain?.[1] || plain?.[2] || "").trim();
  return name || fallback;
};

// Characters no filesystem will take, plus control characters. A company
// name like "AL-KHALEEJ TRADING / SHIPPING L.L.C" is real, and a slash in a
// download name is silently mangled or rejected depending on the browser.
// The control characters are deliberate: a filename carrying one is rejected
// or mangled by the OS, so they are stripped like the rest.
// eslint-disable-next-line no-control-regex
const ILLEGAL = /[/\\:*?"<>|\u0000-\u001f]/g;

/**
 * One filename part, with spaces turned into underscores.
 *
 * Runs of separators collapse to a single underscore, and leading or trailing
 * ones are dropped, so "AL  KHALEEJ / TRADING " does not become
 * "AL__KHALEEJ___TRADING_".
 */
const slug = (value) =>
  String(value ?? "")
    .replace(ILLEGAL, " ")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/_{2,}/g, "_");

/**
 * The date and time, filesystem-safe.
 *
 * Taken from the row's ALREADY FORMATTED `completed` string — "3 Sep 2026,
 * 10:20 AM", in Asia/Dubai — rather than re-formatting the ISO timestamp.
 * briefsApiMapping.js states that formatOrdered and formatCompleted are the
 * only two date formatters in this feature, and a third would be free to
 * disagree with the Completed column the customer is looking at.
 *
 * The comma goes, and the colon becomes a hyphen: Windows and macOS both
 * refuse a colon in a filename, and Finder shows one as a slash.
 */
const stamp = (completed) => slug(String(completed ?? "").replace(/,/g, "").replace(/:/g, "-"));

/**
 * What the downloaded PDF is called.
 *
 *   Gulf_Steel_Trading_FZE_3_Sep_2026_10-20_AM.pdf
 *
 * The target company and the delivery date and time, spaces as underscores.
 * The DELIVERY time, not the moment of the click: it identifies the document
 * rather than the act of fetching it, so downloading the same brief twice
 * gives the same name instead of two differently-named copies of one file.
 *
 * The server's own Content-Disposition name is the fallback, and the bare
 * reference the last resort — a row with no company name should not exist,
 * but it must not produce a file called "download" either.
 */
export const buildDownloadFilename = ({ brief, disposition } = {}) => {
  const company = slug(brief?.company);
  const when = stamp(brief?.completed);

  if (company) {
    const base = when ? `${company}_${when}` : company;
    return `${base}.pdf`;
  }
  return filenameFromDisposition(disposition, brief?.reference || brief?.id);
};

/**
 * Hand the blob to the browser as a download.
 *
 * The object URL is revoked afterwards, because it pins the whole PDF in
 * memory until the tab is closed otherwise — and a customer who downloads
 * several briefs in a session would hold every one of them.
 */
export const saveBlob = (blob, filename, doc = document) => {
  const url = URL.createObjectURL(blob);
  const link = doc.createElement("a");
  link.href = url;
  link.download = filename;
  // Firefox will not act on a click for an anchor outside the document.
  doc.body.appendChild(link);
  link.click();
  doc.body.removeChild(link);
  URL.revokeObjectURL(url);
  return filename;
};

/**
 * Why a download did not happen, in the customer's terms.
 *
 * Keyed on the status, not on the server's sentence: the wording customers
 * read is the frontend's to own, the same way describeOrderError owns the
 * order form's. `staleRow` is the one case that is not really an error — the
 * row said Ready and the server says otherwise, which means the list in hand
 * is out of date and should be refetched rather than merely apologised for.
 */
export const describeDownloadError = (error) => {
  const status = error?.status;
  const copy = TABLE_COPY.downloadError;

  if (status === "FETCH_ERROR" || status === undefined || status === null) {
    return { message: copy.offline, retryable: true, staleRow: false };
  }
  if (status === 401) {
    return { message: copy.signedOut, retryable: false, staleRow: false };
  }
  if (status === 403) {
    // The server's own message is the only thing separating "not ready yet"
    // from "not your company". Both end with the customer not getting a file,
    // but only the first means the row they are looking at is stale.
    const said = String(error?.data?.message || "");
    if (/not ready/i.test(said)) {
      return { message: copy.notReady, retryable: false, staleRow: true };
    }
    return { message: copy.forbidden, retryable: false, staleRow: false };
  }
  if (status === 404) {
    return { message: copy.missing, retryable: false, staleRow: true };
  }
  return { message: copy.failed, retryable: true, staleRow: false };
};
